import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export const clearNavigationWarnings = (callback) => {
  // Remove beforeunload
  if (window._beforeUnloadHandler) {
    window.removeEventListener('beforeunload', window._beforeUnloadHandler);
    delete window._beforeUnloadHandler;
  }

  // Remove popstate
  if (window._popStateHandler) {
    window.removeEventListener('popstate', window._popStateHandler);
    delete window._popStateHandler;

    // If a dummy state exists, remove it safely without blocking navigation
    if (window.history.state?.dummy) {
      // Replace the dummy state instead of going back immediately
      window.history.replaceState(null, "", window.location.href);
    }
  }

  // Remove keydown handler
  if (window._keyDownHandler) {
    document.removeEventListener('keydown', window._keyDownHandler);
    delete window._keyDownHandler;
  }

  // If a callback is provided (e.g., navigate), call it after cleanup
  if (callback) {
    // Use setTimeout to let the history clean-up finish
    setTimeout(callback, 0);
  }
};

/**
 * Custom hook to prevent browser navigation (back, forward, refresh)
 * and show a localized warning message
 *
 * @param {string} warningMessage - The localized warning message to display
 * @param {boolean} enabled - Whether the navigation warning is enabled (default: true)
 */
const useNavigationWarning = (warningMessage, enabled = true) => {
  const location = useLocation();

  // Handle beforeunload event (refresh, close tab)
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = warningMessage;
      return warningMessage;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window._beforeUnloadHandler = handleBeforeUnload;

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      delete window._beforeUnloadHandler;
    };
  }, [warningMessage, enabled]);

  // Handle browser back/forward buttons
  useEffect(() => {
    if (!enabled) return;

    // Push a dummy state to history to intercept back button
    window.history.pushState({ dummy: true }, '', window.location.href);

    const handlePopState = (e) => {
      // Push state again to prevent actual navigation
      window.history.pushState({ dummy: true }, '', window.location.href);

      // Show alert with localized message
      alert(warningMessage);
    };

    window.addEventListener('popstate', handlePopState);
    window._popStateHandler = handlePopState;

    return () => {
      window.removeEventListener('popstate', handlePopState);
      delete window._popStateHandler;
    };
  }, [location, warningMessage, enabled]);

  // Prevent keyboard shortcuts for navigation
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      // Prevent F5 (refresh)
      if (e.key === 'F5') {
        e.preventDefault();
        alert(warningMessage);
        return false;
      }

      // Prevent Ctrl+R or Cmd+R (refresh)
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        alert(warningMessage);
        return false;
      }

      // Prevent Ctrl+Shift+R or Cmd+Shift+R (hard refresh)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
        e.preventDefault();
        alert(warningMessage);
        return false;
      }

      // Prevent Backspace key when not in input field (acts as back button in some browsers)
      if (e.key === 'Backspace' &&
          !['INPUT', 'TEXTAREA'].includes(e.target.tagName) &&
          !e.target.isContentEditable) {
        e.preventDefault();
        alert(warningMessage);
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    window._keyDownHandler = handleKeyDown;

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      delete window._keyDownHandler;
    };
  }, [warningMessage, enabled]);
};

export default useNavigationWarning;
